import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from '../leafletSetup';   // setzt window.L – MUSS vor dem Plugin stehen
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { api } from '../api';

// basemap.de (light) – identisch zu fttbau.de
const TILE = 'https://sgx.geodatenzentrum.de/wmts_basemapde/tile/1.0.0/de_basemapde_web_raster_farbe/default/GLOBAL_WEBMERCATOR/{z}/{y}/{x}.png';

// Auth'd Bild laden: Token im Header → Blob → ObjectURL (gecacht).
// NIE ein öffentliches <img src="/api/uploads/…"> – Bilder bleiben hinter dem Login.
const blobCache = {};
function authImgUrl(name) {
  if (!blobCache[name]) {
    const token = localStorage.getItem('bau_token');
    blobCache[name] = fetch(`/api/uploads/${name}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { if (!r.ok) throw new Error('img'); return r.blob(); })
      .then(b => URL.createObjectURL(b))
      .catch(() => null);
  }
  return blobCache[name];
}

// Lädt alle noch nicht geladenen Bilder (Marker-Kacheln UND Popup-Thumbnails) in einem
// DOM-Bereich per auth Blob-Fetch. Nur was sichtbar/entbündelt ist landet im DOM → begrenzt.
async function fillAuthImgs(root) {
  if (!root) return;
  root.querySelectorAll('img[data-datei]:not([data-loaded])').forEach(async img => {
    img.dataset.loaded = '1';
    const url = await authImgUrl(img.dataset.datei);
    if (url) { img.src = url; img.dataset.src = url; }
  });
}

const fmtDatum = d => { try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; } };
const escAttr = s => (s || '').replace(/"/g, '&quot;').replace(/\n/g, ' ');
const escHtml = s => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function FotoKarte() {
  const navigate = useNavigate();
  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const [photos, setPhotos] = useState(null);      // null = lädt
  const [projekte, setProjekte] = useState([]);
  const [filter, setFilter] = useState('ALLE');
  const [lightbox, setLightbox] = useState(null);  // { src, cap }
  const [error, setError] = useState(null);

  // Daten laden
  useEffect(() => {
    api.get('/bilder/karte')
      .then(all => {
        // defensiv: nur echte Koordinaten (NaN/null/Ausreißer überspringen)
        const rows = all.filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lng));
        setPhotos(rows);
        setProjekte([...new Set(rows.map(r => r.projekt_name))]);
      })
      .catch(e => { setError(e.message || 'Fehler'); setPhotos([]); });
  }, []);

  // Karte einmalig initialisieren
  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    const map = L.map(mapDiv.current, { zoomControl: true }).setView([48.05, 8.35], 11);
    L.tileLayer(TILE, { maxZoom: 19, attribution: '© basemap.de / BKG' }).addTo(map);
    mapRef.current = map;

    // Klick auf Thumbnail → Lightbox (Event-Delegation, Popups sind Plain-DOM)
    map.getContainer().addEventListener('click', e => {
      const t = e.target.closest('.fk-thumb');
      if (t && t.dataset.src) setLightbox({ src: t.dataset.src, cap: t.dataset.cap || '' });
    });

    // Sichtbare Marker-Kacheln + geöffnete Popup-Thumbnails auth'd nachladen
    map.on('popupopen', ev => fillAuthImgs(ev.popup.getElement()));
    map.on('moveend', () => fillAuthImgs(map.getContainer()));

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Marker bei Daten-/Filterwechsel (neu) aufbauen
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !photos) return;
    if (clusterRef.current) { map.removeLayer(clusterRef.current); clusterRef.current = null; }

    const rows = photos.filter(p => filter === 'ALLE' || p.projekt_name === filter);
    const spots = {};
    rows.forEach(p => {
      const key = p.lat.toFixed(5) + '_' + p.lng.toFixed(5);
      (spots[key] = spots[key] || { lat: p.lat, lng: p.lng, fotos: [] }).fotos.push(p);
    });

    // Cluster, wenn Plugin vorhanden; sonst normale Gruppe (kein Absturz).
    // Kleiner Radius + ab Zoom 17 gar kein Clustering → Fotos lösen sich früh in Kacheln auf.
    // Einheitliche orange Cluster-Icons (statt grün/gelb/rot der Standard-Bibliothek).
    const cluster = typeof L.markerClusterGroup === 'function'
      ? L.markerClusterGroup({
          maxClusterRadius: 40,
          spiderfyOnMaxZoom: true,
          spiderfyDistanceMultiplier: 1.8,   // Kacheln beim Auffächern weiter spreizen
          showCoverageOnHover: false,
          iconCreateFunction: c => L.divIcon({
            className: '', iconSize: [40, 40],
            html: `<div class="fk-cluster">${c.getChildCount()}</div>`,
          }),
        })
      : L.layerGroup();
    const bounds = [];
    Object.values(spots).forEach(s => {
      const n = s.fotos.length;
      const first = s.fotos[0];
      const cap = (s.fotos.find(f => f.beschriftung) || {}).beschriftung || '';
      const strip = s.fotos.map(f =>
        `<img class="fk-thumb" data-datei="${f.dateiname}" data-cap="${escAttr(f.beschriftung)}" alt="Foto">`
      ).join('');
      const html =
        `<div class="fk-pop-head">${escHtml(first.projekt_name)}</div>` +
        `<div class="fk-pop-meta">Bericht #${first.nummer ?? '–'} · ${fmtDatum(first.datum)} · ${n} Foto${n > 1 ? 's' : ''}</div>` +
        `<div class="fk-strip">${strip}</div>` +
        (cap ? `<div class="fk-cap">${escHtml(cap)}</div>` : '');
      // Marker = Foto-Kachel (erstes Foto), bei mehreren am Punkt kleine Zahl
      const icon = L.divIcon({
        className: '', iconSize: [46, 46], iconAnchor: [23, 23], popupAnchor: [0, -22],
        html: `<div class="fk-photopin"><img class="fk-mini" data-datei="${first.dateiname}" alt="">${n > 1 ? `<span class="fk-badge">${n}</span>` : ''}</div>`,
      });
      L.marker([s.lat, s.lng], { icon }).bindPopup(html, { maxWidth: 290 }).addTo(cluster);
      bounds.push([s.lat, s.lng]);
    });
    map.addLayer(cluster);
    clusterRef.current = cluster;
    // Nach jedem (Ent-)Bündeln die neu sichtbaren Kacheln laden
    if (typeof cluster.on === 'function') cluster.on('animationend spiderfied', () => fillAuthImgs(map.getContainer()));
    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    fillAuthImgs(map.getContainer());
  }, [photos, filter]);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{FK_CSS}</style>

      {/* Kopfzeile */}
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>← Zurück</button>
        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--accent)' }}>Foto-Karte</div>
        {photos && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>
            {photos.filter(p => filter === 'ALLE' || p.projekt_name === filter).length} Fotos
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['ALLE', ...projekte].map(name => (
            <button key={name} onClick={() => setFilter(name)}
              className="btn btn-sm"
              style={{
                background: filter === name ? 'var(--accent)' : 'var(--bg-2)',
                color: filter === name ? '#fff' : 'var(--text-2)',
                border: '1px solid var(--border)',
              }}>
              {name === 'ALLE' ? 'Alle' : name}
            </button>
          ))}
        </div>
      </div>

      {/* Karte */}
      <div style={{ flex: 1, position: 'relative' }}>
        <div ref={mapDiv} style={{ position: 'absolute', inset: 0 }} />
        {photos === null && !error && (
          <div style={overlayMsg}>Lädt Fotos…</div>
        )}
        {error && <div style={overlayMsg}>Fehler: {error}</div>}
        {photos && photos.length === 0 && !error && (
          <div style={overlayMsg}>Noch keine georeferenzierten Fotos.</div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fk-lb" onClick={() => setLightbox(null)}>
          <span className="fk-lb-x">✕</span>
          <img src={lightbox.src} alt="Foto" onClick={e => e.stopPropagation()} />
          {lightbox.cap && <div className="fk-lb-cap">{lightbox.cap}</div>}
        </div>
      )}
    </div>
  );
}

const overlayMsg = {
  position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 500,
  background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-2)', boxShadow: 'var(--shadow)',
};

const FK_CSS = `
.fk-photopin { width:46px; height:46px; border-radius:10px; overflow:hidden; position:relative;
  border:2.5px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.4); background:var(--bg-3); }
.fk-mini { width:100%; height:100%; object-fit:cover; display:block; }
.fk-badge { position:absolute; top:-7px; right:-7px; min-width:18px; height:18px; padding:0 4px;
  border-radius:9px; background:var(--accent); color:#fff; font-size:0.66rem; font-weight:700;
  display:flex; align-items:center; justify-content:center; border:1.5px solid #fff; font-family:var(--font-mono,monospace); }
.fk-cluster { width:40px; height:40px; border-radius:50%; background:var(--accent); color:#fff;
  display:flex; align-items:center; justify-content:center; font-weight:700; font-size:0.9rem;
  border:2.5px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.4); font-family:var(--font-mono,monospace); }
.leaflet-popup-content { margin:10px 12px; }
.fk-pop-head { font-weight:800; color:var(--text); font-size:0.95rem; }
.fk-pop-meta { font-size:0.7rem; color:var(--text-3); font-family:var(--font-mono,monospace); margin-bottom:8px; }
.fk-strip { display:flex; gap:6px; overflow-x:auto; max-width:262px; padding-bottom:4px; }
.fk-thumb { flex:0 0 auto; width:78px; height:78px; border-radius:8px; object-fit:cover; cursor:pointer;
  border:1px solid var(--border); background:var(--bg-3); }
.fk-cap { font-size:0.72rem; color:var(--text-2); margin-top:7px; max-width:262px; line-height:1.35; white-space:pre-line; }
.fk-lb { position:fixed; inset:0; z-index:3000; background:rgba(0,0,0,0.9); display:flex;
  flex-direction:column; align-items:center; justify-content:center; padding:24px; }
.fk-lb img { max-width:94vw; max-height:80vh; border-radius:8px; box-shadow:0 8px 40px rgba(0,0,0,0.6); }
.fk-lb-cap { color:#f0ece8; font-size:0.9rem; margin-top:14px; max-width:800px; text-align:center; line-height:1.4; white-space:pre-line; }
.fk-lb-x { position:absolute; top:18px; right:22px; color:#fff; font-size:2rem; cursor:pointer; line-height:1; opacity:0.85; }
`;
